import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  SatirDugmesi,
  SatirMetni,
  anMetni,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  entegrasyonAcKapa,
  entegrasyonKaydet,
  entegrasyonTest,
  entegrasyonlar,
  type EntegrasyonDurumu,
  type EntegrasyonSatiri,
} from "./api";
import { useSudoKapisi, useSudoOnce } from "./SudoKapisi";
import { RecaptchaAyar } from "./RecaptchaAyar";

/**
 * ENTEGRASYONLAR — F-TEC-01, F-TEC-02
 *
 * Kurallar sunucuda (`teknik/entegrasyon.ts`); bu dosya yalnizca ekran.
 */

const DURUM_ADI: Record<EntegrasyonDurumu, string> = {
  YAPILANDIRILMADI: "Yapılandırılmadı",
  DENENMEDI: "Denenmedi",
  CALISIYOR: "Çalışıyor",
  HATALI: "Hatalı",
  KAPALI: "Kapalı",
};

/**
 * ================================================================
 * "DENENMEDI" YESIL DEGIL
 * ================================================================
 * Ayarlarin girilmis olmasi calisiyor olmak demek degil. Yesil
 * gosterseydik, hic denenmemis bir baglantiyi calisiyor sanardik ve
 * o yanlisi anlamanin yolu, gercekten ihtiyac duyuldugu an olurdu.
 */
const DURUM_TONU: Record<EntegrasyonDurumu, "notr" | "uyari" | "onay" | "hata"> = {
  YAPILANDIRILMADI: "notr",
  DENENMEDI: "uyari",
  CALISIYOR: "onay",
  HATALI: "hata",
  KAPALI: "notr",
};

const TUR_ADI: Record<string, string> = {
  EMAIL: "E-posta (SMTP)",
  STORAGE: "Dosya depolama",
  RECAPTCHA: "reCAPTCHA",
  WHATSAPP: "WhatsApp",
  SMS: "SMS",
  PAYMENT: "Ödeme",
  E_INVOICE: "e-Fatura",
  GOOGLE_CALENDAR: "Google Takvim",
};

export function Entegrasyonlar() {
  const [secilen, setSecilen] = useState<string | null>(null);
  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "Entegrasyon ayarları hassas; kimliğinizi yeniden doğrulayın.",
  );

  // Pencere kapaliysa sifre ONCE sorulur, liste sorgusu 403 atmaz (UI/UX Dalga 2, M6).
  const { sudoHazir, onKapi } = useSudoOnce(
    "Entegrasyon ayarları hassas; kimliğinizi yeniden doğrulayın.",
  );

  const liste = useQuery<EntegrasyonSatiri[]>({
    queryKey: ["entegrasyonlar"],
    queryFn: entegrasyonlar,
    retry: false,
    enabled: sudoHazir,
  });

  if (onKapi) return <>{onKapi}</>;
  if (liste.isPending) return <Iskelet satir={6} />;
  if (liste.isError) {
    /*
      SUDO ONCE SORULUYOR. Bu ekranin LISTESI de sudo istiyor
      (`sistem.entegrasyon.duzenle`) ve onceden burada yalnizca
      sunucunun mesaji yaziyordu: "sifrenizi yeniden girmeniz
      gerekiyor" diyen, ama sifre girecek yeri OLMAYAN bir ekran.
    */
    const kapiEkrani = sorguKapisi(liste.error, () => void liste.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;

    return (
      <Kart ton="hata" baslik="Entegrasyonlar yüklenemedi">
        <p className="text-notr-700 text-sm">
          {liste.error instanceof ApiHatasi ? liste.error.message : "Bilinmeyen hata."}
        </p>
      </Kart>
    );
  }

  const acik = liste.data.find((e) => e.tur === secilen) ?? null;

  return (
    <div className="flex flex-col gap-4">
      {kapi}
      {acik ? (
        <Ayrinti kayit={acik} hataYakala={hataYakala} onGeri={() => setSecilen(null)} />
      ) : (
        <Liste kayitlar={liste.data} onSec={setSecilen} />
      )}
    </div>
  );
}

function Liste({
  kayitlar,
  onSec,
}: {
  kayitlar: EntegrasyonSatiri[];
  onSec: (tur: string) => void;
}) {
  const bekleyen = kayitlar.filter((e) => e.durum === "DENENMEDI").length;

  return (
    <>
      <Kart
        baslik="Entegrasyonlar"
        aciklama={`${kayitlar.length} tür${bekleyen > 0 ? ` · ${bekleyen} tanesi hiç denenmedi` : ""}`}
        ton={bekleyen > 0 ? "uyari" : "notr"}
      >
        <p className="text-notr-700 text-sm">
          Gizli anahtarlar <strong>yazılır, okunmaz</strong> — girildikten sonra bir daha ekranda
          görünmez. Bir anahtarın değişip değişmediğini iz numarasından anlayabilirsiniz.
        </p>
      </Kart>

      {/*
        reCAPTCHA BURADA, AYRI MENUDE DEGIL: izni bu ekranin izniyle
        AYNI (`sistem.entegrasyon.duzenle`). Ayri bir menu girdisi
        acsaydim iki girdi ayni iki kisiye gorunur ve biri otekini
        aramak zorunda kalirdi.

        Listeye satir olarak da eklenmedi: oteki entegrasyonlar
        "baglanti testi" mantigiyla calisiyor, reCAPTCHA'nin boyle bir
        testi yok — ayni listede durmasi, olmayan bir dugmeyi arattirir.
      */}
      <RecaptchaAyar />

      {/*
        YAPILANDIRILMAMIS TURLER DE LISTEDE. Liste sunucuda semadaki
        turlerden uretiliyor; yalnizca kayitli olanlari gosterseydik
        "WhatsApp neden yok" sorusu "unutulmus" diye okunurdu.
      */}
      <ul className="flex flex-col gap-2">
        {kayitlar.map((e) => (
          <li key={e.tur}>
            <SatirDugmesi onClick={() => onSec(e.tur)}>
              <SatirMetni
                baslik={TUR_ADI[e.tur] ?? e.tur}
                altBaslik={
                  e.testEdilebilir
                    ? e.sonTestAt
                      ? `Son deneme: ${anMetni(e.sonTestAt)}`
                      : "Hiç denenmedi"
                    : "Bağlantı testi yazılmadı"
                }
              />
              <Rozet ton={DURUM_TONU[e.durum]}>{DURUM_ADI[e.durum]}</Rozet>
            </SatirDugmesi>
          </li>
        ))}
      </ul>
    </>
  );
}

function Ayrinti({
  kayit,
  hataYakala,
  onGeri,
}: {
  kayit: EntegrasyonSatiri;
  hataYakala: (tekrarDene: () => void) => (e: unknown) => void;
  onGeri: () => void;
}) {
  const qc = useQueryClient();
  const [ad, setAd] = useState(kayit.ad);
  const [ayarMetni, setAyarMetni] = useState(JSON.stringify(kayit.ayarlar ?? {}, null, 2));
  const [sir, setSir] = useState("");
  /**
   * Sir alani UC HALLI ve bu bilincli:
   *   dokunulmadi  → gonderilmiyor, eskisi korunuyor
   *   yazildi      → yeni sir
   *   "sil" secili → bos metin gonderiliyor, sir siliniyor
   *
   * Ikisini tek bir metin kutusuna indirmek, formu doldurmadan
   * kaydete basan kisinin anahtarini sessizce silmek olurdu.
   */
  const [sirNiyeti, setSirNiyeti] = useState<"dokunma" | "degistir" | "sil">("dokunma");
  const [ayarHatasi, setAyarHatasi] = useState<string | null>(null);

  const tazele = () => void qc.invalidateQueries({ queryKey: ["entegrasyonlar"] });

  const kaydet = useMutation({
    mutationFn: () => {
      let ayarlar: Record<string, unknown>;
      try {
        ayarlar = ayarMetni.trim() === "" ? {} : JSON.parse(ayarMetni);
      } catch {
        throw new Error("Ayarlar geçerli JSON değil.");
      }
      return entegrasyonKaydet({
        tur: kayit.tur,
        ad: ad.trim(),
        ayarlar,
        // `undefined` gönderilirse alan hiç gitmiyor.
        sir: sirNiyeti === "dokunma" ? undefined : sirNiyeti === "sil" ? "" : sir,
      });
    },
    onSuccess: () => {
      setSir("");
      setSirNiyeti("dokunma");
      setAyarHatasi(null);
      tazele();
    },
    onError: hataYakala(() => kaydet.mutate()),
  });

  const test = useMutation({
    mutationFn: () => entegrasyonTest(kayit.tur),
    onSuccess: tazele,
    onError: hataYakala(() => test.mutate()),
  });

  const acKapa = useMutation({
    mutationFn: (acik: boolean) => entegrasyonAcKapa({ tur: kayit.tur, acik }),
    onSuccess: tazele,
    onError: hataYakala(() => acKapa.mutate(kayit.durum === "KAPALI")),
  });

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Buton gorunum="ikincil" onClick={onGeri}>
          ← Entegrasyonlar
        </Buton>
        <h2 className="text-notr-900 text-base font-semibold">{TUR_ADI[kayit.tur] ?? kayit.tur}</h2>
        <Rozet ton={DURUM_TONU[kayit.durum]}>{DURUM_ADI[kayit.durum]}</Rozet>
      </div>

      {kayit.durum === "HATALI" && kayit.sonHata && (
        <Kart ton="hata" baslik="Son deneme başarısız">
          <p className="text-notr-800 text-sm">{kayit.sonHata}</p>
        </Kart>
      )}

      <Kart baslik="Ayarlar">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setAyarHatasi(null);
            kaydet.mutate();
          }}
        >
          <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} />

          <MetinAlani
            etiket="Gizli olmayan ayarlar (uç adresi, hesap adı, bölge)"
            value={ayarMetni}
            onChange={(e) => setAyarMetni(e.target.value)}
            rows={6}
            spellCheck={false}
            daktilo
            yardim="Sağlayıcılar seçilmediği için alan adları türe göre değişiyor; şimdilik serbest JSON."
          />

          <fieldset className="rounded-alan border-notr-200 flex flex-col gap-2 border p-3">
            <legend className="text-notr-700 px-1 text-sm">Gizli anahtar</legend>

            <p className="text-notr-600 text-sm">
              {kayit.sirVar ? (
                <>
                  Kayıtlı: <span className="font-mono">{kayit.maske}</span>{" "}
                  <span className="text-notr-600 text-xs">(iz: {kayit.sirIzi})</span>
                </>
              ) : (
                "Kayıtlı anahtar yok."
              )}
            </p>

            <div className="flex flex-wrap gap-3 text-sm">
              {(["dokunma", "degistir", "sil"] as const).map((n) => (
                <label key={n} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="sir-niyeti"
                    checked={sirNiyeti === n}
                    onChange={() => setSirNiyeti(n)}
                    disabled={n === "sil" && !kayit.sirVar}
                  />
                  <span className="text-notr-700">
                    {n === "dokunma"
                      ? "Dokunma"
                      : n === "degistir"
                        ? "Yeni anahtar gir"
                        : "Anahtarı sil"}
                  </span>
                </label>
              ))}
            </div>

            {sirNiyeti === "degistir" && (
              <Alan
                etiket="Yeni gizli anahtar"
                type="password"
                autoComplete="off"
                value={sir}
                onChange={(e) => setSir(e.target.value)}
                className="font-mono"
              />
            )}

            <p className="text-notr-600 text-xs">
              Anahtar kaydedildikten sonra bir daha gösterilmez. İz numarası yalnızca &quot;değişti
              mi&quot; sorusunu cevaplar.
            </p>
          </fieldset>

          {(ayarHatasi || kaydet.isError) && (
            <p className="text-hata-500 text-sm">
              {ayarHatasi ??
                (kaydet.error instanceof ApiHatasi
                  ? kaydet.error.message
                  : kaydet.error instanceof Error
                    ? kaydet.error.message
                    : "Kaydedilemedi.")}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Buton type="submit" disabled={kaydet.isPending}>
              {kaydet.isPending ? "Kaydediliyor…" : "Kaydet"}
            </Buton>

            {/*
              TEST DUGMESI YALNIZCA TESTI YAZILAN TURLERDE. Saglayicisi
              secilmemis turlerde her zaman basarisiz donen bir test
              gostermek, kullaniciyi olmayan bir arizayi aramaya iterdi.
            */}
            {kayit.testEdilebilir ? (
              <Buton gorunum="ikincil" type="button" onClick={() => test.mutate()}>
                {test.isPending ? "Deneniyor…" : "Bağlantıyı dene"}
              </Buton>
            ) : null}

            {kayit.durum !== "YAPILANDIRILMADI" && (
              <Buton
                gorunum="sessiz"
                type="button"
                onClick={() => acKapa.mutate(kayit.durum === "KAPALI")}
              >
                {kayit.durum === "KAPALI" ? "Aç" : "Kapat"}
              </Buton>
            )}
          </div>

          {!kayit.testEdilebilir && kayit.testYokSebebi && (
            <p className="text-notr-600 text-sm">
              <strong>Bağlantı testi yok.</strong> {kayit.testYokSebebi}
            </p>
          )}

          {test.isSuccess && (
            <p className={`text-sm ${test.data.basarili ? "text-onay-700" : "text-hata-500"}`}>
              {test.data.mesaj}
            </p>
          )}
          {test.isError && (
            <p className="text-hata-500 text-sm">
              {test.error instanceof ApiHatasi ? test.error.message : "Deneme yapılamadı."}
            </p>
          )}
          {acKapa.isError && (
            <p className="text-hata-500 text-sm">
              {acKapa.error instanceof ApiHatasi ? acKapa.error.message : "Değiştirilemedi."}
            </p>
          )}
        </form>
      </Kart>
    </>
  );
}
